/**
 * The "More ▾" dropdown, rendered from the toolbar model's grouped
 * descriptors.
 *
 * Separators are emitted between groups rather than authored per group, so a
 * gated-off group (DBA, today) takes its separator with it — the DOM sequence
 * is identical to the hand-written menu this replaced and is pinned by
 * `MainToolbar.moreMenuParity.test.tsx`.
 */
import { Fragment } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { resolveToolbarAction } from '../model/mainToolbarModel';
import type { ToolbarItemDescriptor, ToolbarMoreGroup } from '../model/toolbarItemTypes';

function ToolbarMoreMenuItem({ item }: { item: ToolbarItemDescriptor }) {
  const Icon = item.icon;
  return (
    <DropdownMenuItem
      onClick={resolveToolbarAction(item)}
      disabled={item.disabled}
      title={item.tooltip}
      data-testid={item.testId}
    >
      {Icon && <Icon className="h-4 w-4 mr-2" />}
      {item.label}
      {item.note && <span className="ml-auto text-xs text-muted-foreground">{item.note}</span>}
    </DropdownMenuItem>
  );
}

type ToolbarMoreMenuProps = {
  /** The `more` descriptor — supplies the trigger's label, tooltip and testid. */
  trigger: ToolbarItemDescriptor;
  groups: ToolbarMoreGroup[];
  onOpen: () => void;
};

export function ToolbarMoreMenu({ trigger, groups, onOpen }: ToolbarMoreMenuProps) {
  const TriggerIcon = trigger.icon;
  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) onOpen();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          className="group flex items-center gap-1.5 px-2 py-1 text-sm rounded-sm hover:bg-muted transition-[color,background-color,scale] active:scale-[0.96] relative before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-['']"
          title={trigger.tooltip}
          data-toolbar-item
          data-testid={trigger.testId}
        >
          {TriggerIcon && <TriggerIcon className="h-4 w-4" />}
          <span>{trigger.label}</span>
          <ChevronDown className="h-3 w-3 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {groups.map((group, index) => (
          <Fragment key={group.id}>
            {index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                {group.label}
              </DropdownMenuLabel>
              {group.items.map((item) => (
                <ToolbarMoreMenuItem key={item.id} item={item} />
              ))}
            </DropdownMenuGroup>
          </Fragment>
        ))}

        {/* Non-interactive signpost to the richer right-click path (which
            already carries Set-as-Source / Set-as-Target) — matches the
            muted-footer treatment used elsewhere (e.g. CommandPalette's
            Shift+Enter hint). */}
        <div className="-mx-1 mt-1 border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">
          Tip: right-click a database in the Explorer for Schema Compare, Script, Blueprint…
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
